import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { AppLayout } from './components/layout/AppLayout';
import { Sets } from './pages/sets/Sets';
import { SetDetail } from './pages/sets/SetDetail';
import { CardDetail } from './pages/cards/CardDetail';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { Landing } from './pages/Landing';
import { Decks } from './pages/decks/Decks';
import { PublicDecks } from './pages/decks/PublicDecks';
import { DeckDetail } from './pages/decks/DeckDetail';
import { CreateDeck } from './pages/decks/CreateDeck';
import { EditDeck } from './pages/decks/EditDeck';
import { LegalNotice } from './pages/legal/LegalNotice';
import { Privacy } from './pages/legal/Privacy';
import { Profile } from './pages/user/Profile';
import { UserCollection } from './pages/user/UserCollection';
import { FriendCodes } from './pages/user/FriendCodes';
import { useAuth } from './hooks/useAuth';
import { GoogleAnalytics } from './components/analytics/GoogleAnalytics';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { News } from './pages/news/News';
import { Article } from './pages/news/Article';
import { Queue } from './components/pvp/Queue';
import { Game } from './components/pvp/Game';
import { Ranking } from './pages/pvp/Ranking';
import { PublicProfile } from './pages/user/PublicProfile';
import { AdminTradingSettings } from './pages/admin/AdminTradingSettings';
import { CreateTrade } from './pages/trades/CreateTrade';
import { MyTrades } from './pages/trades/MyTrades';
import { BrowseTrades } from './pages/trades/BrowseTrades';
import { AcceptTrade } from './pages/trades/AcceptTrade';
import { EditTrade } from './pages/trades/EditTrade';
import { TradeDetail } from './pages/trades/TradeDetail';
import { TradeInfo } from './pages/trades/TradeInfo';
import AdminMissions from './pages/admin/AdminMissions';
import CreateEditMission from './pages/admin/CreateEditMission';
import AdminRewards from './pages/admin/AdminRewards';
import AdminDashboard from './pages/admin/AdminDashboard';
import { RequireAdmin } from './components/auth/RequireAdmin';
import { MissionDetail } from './pages/missions/MissionDetail';
import { DeckChoice } from './pages/decks/DeckChoice';
import { PvpChoice } from './pages/pvp/PvpChoice';
import { TradeChoice } from './pages/trades/TradeChoice';
import { OtherChoice } from './pages/other/OtherChoice';
import { useState, useEffect } from 'react';
import { initializeFirebaseMessaging } from './lib/firebase';
import { Notifications } from './pages/notifications/Notifications';
import { ScrollToTop } from './components/utils/ScrollToTop';
import { Contact } from './pages/contact/Contact';
import { About } from './pages/about/About';
import { MessagesContainer } from './pages/messages/MessagesContainer';
import { useMessageNotifications } from './hooks/useMessageNotifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TradeSettingsProvider } from './contexts/TradeSettingsContext';
import { HelmetProvider } from 'react-helmet-async';
import InstallPrompt from './components/pwa/InstallPrompt';
import IOSInstallPrompt from './components/pwa/IOSInstallPrompt';
import PokemonLuck from './pages/pokemon-luck/PokemonLuck';
import { AdSenseLoader } from './components/ads/AdSense';
import { CardScanner } from './pages/CardScanner';
import { I18nProvider, useI18n } from './contexts/I18nContext';

function OfflineAlert() {
  const { t } = useI18n();
  
  return (
    <div className="bg-yellow-50 p-3 fixed bottom-0 left-0 right-0 z-50 text-center">
      <p className="text-yellow-800">
        {t('app.offline')}
      </p>
    </div>
  );
}

// Composant pour définir les routes, utilisé à la fois pour les routes normales et pour /en/
const AppRoutes = () => {
  const { user, loading } = useAuth();
  const { getPathForLang } = useI18n();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to={getPathForLang("/sets")} /> : <Landing />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={!user ? <Login /> : <Navigate to={getPathForLang("/sets")} />} />
      <Route path="/register" element={!user ? <Register /> : <Navigate to={getPathForLang("/sets")} />} />
      <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to={getPathForLang("/forgot-password")} />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Legal Routes */}
      <Route path="/mentions-legales" element={<LegalNotice />} />
      <Route path="/confidentialite" element={<Privacy />} />

      {/* Sets Routes */}
      <Route path="/sets" element={user ? <Sets /> : <Navigate to="/" />} />
      <Route path="/sets/:code" element={user ? <SetDetail /> : <Navigate to="/login" />} />
      <Route path="/sets/:code/cards/:id" element={user ? <CardDetail /> : <Navigate to="/login" />} />
      <Route path="/sets/:code/missions/:missionId" element={<MissionDetail />} />
      <Route path="/card-scanner" element={user ? <CardScanner /> : <Navigate to="/login" />} />

      {/* Decks Routes */}
      <Route path="/decks-choice" element={<DeckChoice />} />
      <Route path="/decks" element={user ? <Decks /> : <Navigate to="/login" />} />
      <Route path="/public-decks" element={<PublicDecks />} />
      <Route path="/decks/new" element={user ? <CreateDeck /> : <Navigate to="/login" />} />
      <Route path="/decks/:id" element={<DeckDetail />} />
      <Route path="/decks/:id/edit" element={user ? <EditDeck /> : <Navigate to="/login" />} />

      {/* Friend Codes Routes */}
      <Route path="/friend-codes" element={<FriendCodes />} />

      {/* News Routes */}
      <Route path="/actualites" element={<News />} />
      <Route path="/actualites/:slug" element={<Article />} />
      
      {/* PvP Routes */}
      <Route path="/pvp-choice" element={<PvpChoice />} />
      <Route path="/pvp/queue" element={user ? <Queue /> : <Navigate to="/login" />} />
      <Route path="/pvp/ranking" element={user ? <Ranking /> : <Navigate to="/login" />} />
      <Route path="/pvp/match/:password" element={user ? <Game /> : <Navigate to="/login" />} />
      
      {/* Profile Routes */}
      <Route path="/users/:username" element={<PublicProfile />} />
      <Route path="/users/:username/collection" element={user ? <UserCollection /> : <Navigate to="/login" />} />
      <Route path="/profile" element={user ? <Profile /> : <Navigate to="/login" />} />
      
      {/* Trading Routes */}
      <Route path="/trades-choice" element={<TradeChoice />} />
      <Route path="/trades/create" element={user ? <CreateTrade /> : <Navigate to="/login" />} />
      <Route path="/trades/my-trades" element={user ? <MyTrades /> : <Navigate to="/login" />} />
      <Route path="/trades/browse" element={<BrowseTrades />} />
      <Route path="/trades/:tradeId/info" element={<TradeInfo />} />
      <Route path="/trades/:tradeId/accept" element={user ? <AcceptTrade /> : <Navigate to="/login" />} />
      <Route path="/trades/:id/edit" element={user ? <EditTrade /> : <Navigate to="/login" />} />
      <Route path="/trades/:id" element={user ? <TradeDetail /> : <Navigate to="/login" />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
      <Route path="/admin/missions" element={<RequireAdmin><AdminMissions /></RequireAdmin>} />
      <Route path="/admin/missions/create" element={<RequireAdmin><CreateEditMission /></RequireAdmin>} />
      <Route path="/admin/missions/:id/edit" element={<RequireAdmin><CreateEditMission /></RequireAdmin>} />
      <Route path="/admin/rewards" element={<RequireAdmin><AdminRewards /></RequireAdmin>} />
      <Route path="/admin/trading-settings" element={<RequireAdmin><AdminTradingSettings /></RequireAdmin>} />

      {/* Other Routes */}
      <Route path="/other-choice" element={user ? <OtherChoice /> : <Navigate to="/login" />} />

      {/* Contact Route */}
      <Route path="/contact" element={<Contact />} />

      {/* Notifications Route */}
      <Route path="/notifications" element={user ? <Notifications /> : <Navigate to="/login" />} />

      {/* About Route */}
      <Route path="/about" element={<About />} />

      {/* Pokemon Luck Calculator Route */}
      <Route path="/pokemon-luck" element={<PokemonLuck />} />

      {/* Messages Routes */}
      <Route 
        path="/messages/*" 
        element={user ? <MessagesContainer /> : <Navigate to="/login" />} 
      />

      {/* Default Route */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export default function App() {
  const { user, loading } = useAuth();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const queryClient = new QueryClient();

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (user) {
      console.log('Initialisation de Firebase pour l\'utilisateur:', user.id);
      initializeFirebaseMessaging().catch(console.error);
    }
  }, [user]);

  return (
    <Router>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TradeSettingsProvider>
            <I18nProvider>
              <AdSenseLoader>
                {user && <MessageNotificationsHandler />}
                <ScrollToTop />
                <GoogleAnalytics />
                <div className="min-h-screen bg-gray-100 flex flex-col">
                  <Header />
                  <main className="flex-1 pb-20 md:pb-0">
                    <AppLayout>
                      <Routes>
                        {/* Routes avec préfixe en anglais */}
                        <Route path="/en/*" element={<AppRoutes />} />
                        
                        {/* Routes normales en français (par défaut) */}
                        <Route path="/*" element={<AppRoutes />} />
                      </Routes>
                    </AppLayout>
                  </main>
                  <Footer />
                  {isOffline && <OfflineAlert />}
                  <InstallPrompt />
                  <IOSInstallPrompt />
                </div>
              </AdSenseLoader>
            </I18nProvider>
          </TradeSettingsProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </Router>
  );
}

// Composant pour gérer les notifications
const MessageNotificationsHandler = () => {
  useMessageNotifications();
  return null;
};